/* eslint-disable consistent-return */
import { getAuthenticationState } from 'lib/authenticationUtils';

/**
 * Creates Piano event handlers with proper logging and integration.
 * Keep all caller-supplied callbacks in `handlers` untouched.
 *
 * @param {object} handlers – Custom hooks from the consumer (all optional)
 * @param {object} deps – shared utilities & refs from the provider
 */
export const createPianoEventHandlers = (
  handlers = {},
  deps,
) => {
  const {
    authenticate,
    trackEvent,
    pendingCheckoutRef,
    templateShownRef,
    userInteractedRef,
    startCheckout,
  } = deps;

  /**
   * Safely invokes a function if it is defined, passing any additional arguments to it.
   *
   * @param {Function} fn - The function to invoke.
   * @param {...*} args - Arguments to pass to the function.
   */
  const invokeIfDefined = (fn, ...args) => { if (typeof fn === 'function') fn(...args); };

  /**
   * Handles the start of the checkout process by tracking the event and invoking the corresponding handler.
   *
   * @param {object} e - The event object containing checkout details.
   * @param {string} [e.termId] - The identifier for the selected term (optional).
   * @param {string} [e.offerId] - The identifier for the selected offer (optional).
   */
  const handleCheckoutStart = (e) => {
    trackEvent('piano_checkout_start', { termId: e?.termId, offerId: e?.offerId });
    invokeIfDefined(handlers.onCheckoutStart, e);
  };

  /**
   * Handles the completion of a checkout process.
   * Tracks the checkout completion event and invokes the onCheckoutComplete handler.
   *
   * @param {object} conversion - The conversion details.
   * @param {string|number} conversion.termConversionId - The ID of the term conversion.
   * @param {number} conversion.chargeAmount - The amount charged during checkout.
   * @param {string} conversion.chargeCurrency - The currency of the charge.
   */
  const handleCheckoutComplete = (conversion) => {
    trackEvent('piano_checkout_complete', {
      termConversionId: conversion.termConversionId,
      chargeAmount: conversion.chargeAmount,
      chargeCurrency: conversion.chargeCurrency,
    });
    invokeIfDefined(handlers.onCheckoutComplete, conversion);
  };

  /**
   * Handles the event when the checkout is closed.
   *
   * Tracks the 'piano_checkout_close' event with the current state and invokes the onCheckoutClose handler.
   *
   * @param {object} e - The event object containing checkout state information.
   * @param {string} e.state - The current state of the checkout when closed.
   */
  const handleCheckoutClose = (e) => {
    trackEvent('piano_checkout_close', { state: e.state });
    invokeIfDefined(handlers.onCheckoutClose, e);
  };

  /**
   * Handles changes in the checkout state by tracking the event and invoking the corresponding handler.
   *
   * @param {object} e - The event object containing information about the checkout state change.
   * @param {string} e.stateName - The name of the new checkout state.
   */
  const handleCheckoutStateChange = (e) => {
    trackEvent('piano_checkout_state_change', { state: e.stateName });
    invokeIfDefined(handlers.onCheckoutStateChange, e);
  };

  /**
   * Handles a custom checkout event by marking user interaction,
   * tracking the event for analytics, and invoking the provided handler.
   *
   * @param {object} event - The custom event object.
   * @param {string} event.eventName - The name of the custom event.
   */
  const handleCheckoutCustomEvent = (event) => {
    userInteractedRef.current = true;
    trackEvent('piano_template_event', { name: event.eventName });
    if (event.eventName === 'sign-in') {
      authenticate();
    }
    invokeIfDefined(handlers.onCheckoutCustomEvent, event);
  };


  /**
   * Handles the login required event for the Piano integration.
   * - If the user is already authenticated, starts the checkout process.
   * - If a template is shown but the user hasn't interacted, suppresses the login required action.
   * - Otherwise, tracks the event, closes any open Piano modals/offers, and triggers authentication.
   *
   * @param {object} params - Parameters related to the checkout or login event.
   * @returns {boolean} Always returns false to indicate further processing should stop.
   */
  const handleLoginRequired = (params) => {
    if (getAuthenticationState() === 'authenticated') {
      startCheckout(params);
      return false;
    }

    if (templateShownRef.current && !userInteractedRef.current) {
      return false;
    }

    pendingCheckoutRef.current = params;
    trackEvent('piano_login_required', params);
    window.tp?.push?.(['customEvent', { eventName: 'bedrock-auth-toggle', isShown: true }]); // toggle auth modal
    window.tp?.modal?.close?.();
    window.tp?.offer?.close?.();

    authenticate();
    return false;
  };


  /**
   * Handles the successful login event.
   *
   * Tracks the 'piano_login_success' event with the provided source,
   * and invokes the onLoginSuccess handler with the login data.
   *
   * @param {object} data - The login data.
   * @param {string} data.source - The source of the login event.
   */
  const handleLoginSuccess = (data) => {
    trackEvent('piano_login_success', { source: data.source });
    invokeIfDefined(handlers.onLoginSuccess, data);
  };

  /**
   * Handles the event when a piano template is shown.
   * Sets the templateShownRef to true, tracks the event, and calls the onShowTemplate handler.
   *
   * @param {object} params - The template data.
   * @param {string|number} params.templateId - The unique identifier of the template being shown.
   */
  const handleShowTemplate = (params) => {
    templateShownRef.current = true;
    trackEvent('piano_template_shown', { templateId: params.templateId });
    invokeIfDefined(handlers.onShowTemplate, params);
  };

  /**
   * Handles the event when a Piano offer is shown.
   * Tracks the 'piano_offer_shown' event and invokes the onShowOffer handler.
   *
   * @param {object} params - The offer parameters.
   * @param {string|number} params.offerId - The unique identifier of the offer being shown.
   */
  const handleShowOffer = (params) => {
    trackEvent('piano_offer_shown', { offerId: params.offerId });
  };

  /**
   * Handles the execution of a piano experience event.
   * Tracks the 'piano_experience_execute' event with the associated experience ID,
   * and invokes the provided onExperienceExecute handler.
   *
   * @param {object} e - The event object containing experience execution details.
   * @param {object} e.result - The result object from the event.
   * @param {Array} [e.result.experiences] - Array of experience objects.
   * @param {string} [e.result.experiences.0.id] - The ID of the executed experience.
   */
  const handleExperienceExecute = (e) => {
    trackEvent('piano_experience_execute', { experienceId: e.result?.experiences?.[0]?.id });
    invokeIfDefined(handlers.onExperienceExecute, e);
  };

  return {
    handleCheckoutStart,
    handleCheckoutComplete,
    handleCheckoutClose,
    handleCheckoutCustomEvent,
    handleCheckoutStateChange,
    handleLoginRequired,
    handleLoginSuccess,
    handleShowTemplate,
    handleShowOffer,
    handleExperienceExecute,
  };
};
